// gti-chms-frontend/src/components/AccountFormModal.tsx
import React, { useState, useEffect } from 'react';
import type { Account } from '../../models'; // Import the Account interface

// Define form data type, excluding auto-generated/managed fields
interface AccountFormData {
    name: string;
    type: 'Bank' | 'MobileMoney' | ''; // Allow empty for initial selection
    provider: string;
    accountIdentifier: string;
    currency: string;
    initialBalance: number;
    purpose: string;
    isActive: boolean;
}

interface AccountFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (accountData: AccountFormData, accountId?: string) => Promise<void>;
    accountToEdit?: Account | null; // Optional: account data if in edit mode
}

const AccountFormModal: React.FC<AccountFormModalProps> = ({ isOpen, onClose, onSave, accountToEdit }) => {
    const isEditing = !!accountToEdit;
    const [formData, setFormData] = useState<AccountFormData>({
        name: '',
        type: '',
        provider: '',
        accountIdentifier: '',
        currency: 'SZL',
        initialBalance: 0,
        purpose: '',
        isActive: true,
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Populate form data when accountToEdit changes (for edit mode)
    useEffect(() => {
        if (isEditing && accountToEdit) {
            setFormData({
                name: accountToEdit.name,
                type: accountToEdit.type,
                provider: accountToEdit.provider,
                accountIdentifier: accountToEdit.accountIdentifier,
                currency: accountToEdit.currency,
                initialBalance: accountToEdit.initialBalance, // Only shown in edit, not directly editable to update balance
                purpose: accountToEdit.purpose || '',
                isActive: accountToEdit.isActive,
            });
        } else {
            // Reset form for add mode
            setFormData({
                name: '',
                type: '',
                provider: '',
                accountIdentifier: '',
                currency: 'SZL',
                initialBalance: 0,
                purpose: '',
                isActive: true,
            });
        }
        setError(null);
        setSuccess(null);
    }, [isOpen, isEditing, accountToEdit]); // Re-run when modal opens or accountToEdit changes

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else if (name === 'initialBalance') {
            setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setLoading(true);

        // Basic client-side validation
        if (!formData.name || !formData.type || !formData.provider || !formData.accountIdentifier || !formData.currency) {
            setError('Please fill in all required fields.');
            setLoading(false);
            return;
        }
        if (!isEditing && (typeof formData.initialBalance !== 'number' || formData.initialBalance < 0)) {
            setError('Initial balance must be a non-negative number.');
            setLoading(false);
            return;
        }

        try {
            // For update, we only send fields that are editable.
            // currentBalance, createdBy, createdAt, updatedAt, churchId are handled by backend.
            // initialBalance is only sent on create.
            const dataToSend: Partial<AccountFormData> = { ...formData };
            if (isEditing) {
                // Remove initialBalance from update payload
                delete dataToSend.initialBalance;
            }

            await onSave(dataToSend as AccountFormData, isEditing ? accountToEdit?._id : undefined);
            setSuccess(`Account ${isEditing ? 'updated' : 'added'} successfully!`);
            setTimeout(() => {
                onClose(); // Close modal after success
            }, 1500);
        } catch (err: any) {
            console.error("Save Account Error:", err);
            setError(err.response?.data?.message || err.message || `Failed to ${isEditing ? 'update' : 'add'} account.`);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
            <div className="relative p-8 bg-white w-full max-w-2xl mx-auto rounded-lg shadow-xl animate-scale-in">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
                >
                    &times;
                </button>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                    {isEditing ? 'Edit Account' : 'Add New Account'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Account Name</label>
                            <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                        </div>
                        <div>
                            <label htmlFor="type" className="block text-sm font-medium text-gray-700">Account Type</label>
                            <select name="type" id="type" value={formData.type} onChange={handleChange} required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                                <option value="">Select Type</option>
                                <option value="Bank">Bank</option>
                                <option value="MobileMoney">Mobile Money</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="provider" className="block text-sm font-medium text-gray-700">Provider</label>
                            <input type="text" name="provider" id="provider" value={formData.provider} onChange={handleChange} required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                        </div>
                        <div>
                            <label htmlFor="accountIdentifier" className="block text-sm font-medium text-gray-700">Account Number / ID</label>
                            <input type="text" name="accountIdentifier" id="accountIdentifier" value={formData.accountIdentifier} onChange={handleChange} required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                        </div>
                        {!isEditing && ( // Initial Balance only for new accounts
                            <div>
                                <label htmlFor="initialBalance" className="block text-sm font-medium text-gray-700">Initial Balance</label>
                                <input type="number" name="initialBalance" id="initialBalance" value={formData.initialBalance} onChange={handleChange} required
                                    min="0" step="0.01"
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                            </div>
                        )}
                        <div>
                            <label htmlFor="purpose" className="block text-sm font-medium text-gray-700">Purpose (Optional)</label>
                            <textarea name="purpose" id="purpose" value={formData.purpose} onChange={handleChange} rows={2}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                        </div>
                        <div className="flex items-center space-x-2 pt-4">
                            <input type="checkbox" name="isActive" id="isActive" checked={formData.isActive} onChange={handleChange}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Account Active</label>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm" role="alert">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative text-sm" role="alert">
                            {success}
                        </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-4">
                        <button type="button" onClick={onClose} disabled={loading}
                            className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading}
                            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50">
                            {loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Saving...
                                </span>
                            ) : (
                                isEditing ? 'Update Account' : 'Add Account'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AccountFormModal;
